import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, X } from 'lucide-react';

const plans = [
  {
    name: 'Free',
    price: 0,
    period: 'forever',
    description: 'Perfect for occasional signing',
    features: [
      '3 documents/month',
      '1 email signature',
      'Basic templates',
      'Email support',
      'Audit log',
    ],
    notIncluded: ['Unlimited documents', 'Animated signatures', 'Social banners', 'AI images'],
    cta: 'Start Free',
    to: '/register',
    highlight: false,
  },
  {
    name: 'Pro',
    price: 19,
    period: 'month',
    description: 'For professionals & freelancers',
    features: [
      'Unlimited documents',
      '10 email signatures',
      '10 AI images/month',
      'All 7 animation types',
      'Social media banners',
      'GIF & HTML export',
      'Priority support',
    ],
    notIncluded: [],
    cta: 'Start Pro',
    to: '/register?plan=pro',
    highlight: true,
  },
  {
    name: 'Business',
    price: 49,
    period: 'month',
    description: 'For teams & power users',
    features: [
      'Everything in Pro',
      'Unlimited email signatures',
      '50 AI images/month',
      'Team collaboration',
      'Custom branding',
      'API access',
      'Dedicated support',
    ],
    notIncluded: [],
    cta: 'Start Business',
    to: '/register?plan=business',
    highlight: false,
  },
];

export default function Pricing() {
  return (
    <div className="py-20 bg-gray-50">
      <div className="max-w-5xl mx-auto px-4">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Simple, Transparent Pricing</h1>
          <p className="text-xl text-gray-500">Start free. Upgrade when you need more.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map(plan => (
            <div
              key={plan.name}
              className={`bg-white rounded-2xl p-8 border ${
                plan.highlight
                  ? 'border-blue-500 shadow-lg ring-2 ring-blue-500'
                  : 'border-gray-200 shadow-sm'
              }`}
            >
              {plan.highlight && (
                <div className="bg-blue-600 text-white text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full inline-block mb-4">
                  Most Popular
                </div>
              )}
              <h2 className="text-2xl font-bold text-gray-900">{plan.name}</h2>
              <p className="text-gray-500 text-sm mt-1 mb-4">{plan.description}</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900">${plan.price}</span>
                <span className="text-gray-500">/{plan.period}</span>
              </div>
              <Link
                to={plan.to}
                className={`block text-center py-3 rounded-xl font-medium mb-6 transition-colors ${
                  plan.highlight
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }`}
              >
                {plan.cta}
              </Link>
              <ul className="space-y-2.5">
                {plan.features.map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" /> {f}
                  </li>
                ))}
                {plan.notIncluded.map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm text-gray-400">
                    <X className="w-4 h-4 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center text-sm text-gray-500">
          All plans include SSL encryption, audit trails, and email delivery.
          Cancel anytime. Questions?{' '}
          <a href="mailto:support@ezsig.com" className="text-blue-600 hover:underline">Contact us</a>
        </div>
      </div>
    </div>
  );
}
